// THE LINE-ITEMS TABLE, EVERYWHERE ONE IS SHOWN (app.md §8): the Import
// Control editor, the approval tiers, Export Control, Manual Entry, the
// read-only modal steps and the expanded queue card. Every column ruling —
// which columns, in what order, under what header, what each icon does, the
// bulk row and the footer — lands here once (code review D-13,
// D-284). Until 2026-10-03 the editor
// drew a second copy of this table of its own.
//
// Two row sources, one row model (`TableLine`):
//
// - READ: the stored lines (`lineItems`). A cell is a picker where the caller
//   passes that half's write (`onAccountChange`, `onDepartmentChange`), a
//   category picker where it passes `onCategoryChange`, and a shaded locked box
//   or plain text otherwise (`LockedValue.tsx`).
// - EDIT: the Import Control editor's rows (`editor`), which the editor owns
//   and saves. Description, Price and Quantity are inputs, Account and
//   Department pickers, and each row adds Delete; an Add Line Item button sits
//   under the table, and the bulk row offers Group and Delete (D-228 to D-230,
//   D-242, D-407).
//
// The footer is a pass/fail "Reconciles" / "Doesn't reconcile" pill, then
// "Invoice Subtotal:" and the summed lines (owner, 2026-08-14). The check is
// the release gate's: lines + tax + shipping against the Total, within three
// cents (D-151). Its "View With Tax & Shipping Distributed" toggle is a view
// preference of its own (owner, 2026-08-14): on, two locked columns show each
// line's share of the two header charges — the Point Click Care CSV's own
// figure for that line, cent for cent (D-40, D-151) — and the line's Total
// carries both.
//
// LINE ITEM GROUPS (owner, 2026-10-22 — D-396, D-399; G-19 to G-21). A group
// row stands over two or more lines: a BLUE expand arrow at its left, no row
// fill (D-408 dropped D-407's gray), its lines' Descriptions indented under
// it and their drag handles neutral where a top-level line's is blue (D-408),
// collapsed by default, with one Expand All / Collapse All icon in the header cell above the
// arrows — the queues' own double chevrons and words. The three leftmost marks
// — Expand All / Collapse All, a group's arrow and a line's drag handle — share
// one box, size and colour (`useLineMarkStyles`, D-407). Its Account,
// Department and Marketing Category are LOCKED at every step and read from its
// lines (`groupFieldViews`, the server's own rule): the value only once every
// line holds it, blank with a red border otherwise (D-406, superseding G-7's
// setting them on the group and G-8's showing a value some lines lacked). Its
// Description is the one field it edits. Its price is its lines' sum and its
// quantity 1. A collapsed group shows a flag icon when one of its lines carries
// an unresolved flag (G-10). Where lines can be checked, a group row's box
// checks or unchecks all its lines (D-406). Where grouping may change
// (`groupingOpen`, G-12) every line shows a drag handle where a group row shows
// its arrow: dropped on a group row or one of its lines it joins that group;
// dropped on a line standing alone the two make a new group with an empty,
// required Description (D-406); dropped anywhere that is not a row, a line in a
// group leaves it (D-407). The bulk row offers Group, Ungroup and
// Move to Existing Group (G-20; Group was named Combine until D-407). The reconcile footer
// sums the real lines; the tax and shipping shares go on the rows the CSV
// exports — a group row in place of its lines (D-403).
//
// THE TAX AND SHIPPING GROUPS (owner, 2026-10-27 — D-420: "keep all charges
// together under Line Items Summary"). Every invoice shows a Tax group and a
// Shipping group, at the BOTTOM of the table, after the ordinary groups and
// the lines standing alone, Tax then Shipping — drawn as an empty placeholder
// when the server has not stored one yet ("I want to have a group ready down
// there ... I don't want to count on them to spell 'Tax' right"). A charge
// group's name is fixed, it has no Remove Group, and its Account, Department
// and Marketing Category are locked and blank, never red; so are its lines'.
// A line joins one by a drag onto the group row or one of its lines, or by
// Move to Existing Group, either of which sends `{ op: 'move', charge_kind }`
// (the server makes the group when the invoice has none); it leaves as it
// leaves any group. The distributed view spreads the charge lines (and any old
// header Tax or Shipping) over the coded rows alone, as the CSV does.
//
// SHOW GROUP ROWS (owner, 2026-10-22): a footer switch at the left end of the
// footer, apart from View With Tax & Shipping Distributed but built the same
// way — a view preference, on at every mount, never sent. Owner: "When they are hidden, I would want to see all of
// the rows in order, like they are on the invoice or receipt. When they are
// shown, I would want to see those parent/child groupings, whether they are
// collapsed or expanded." Hidden: every real line, flat, by line number, no
// fill and no indent, each as editable as inside its group; no drag and no
// Expand All / Collapse All. A grouped line's charge share is its group's,
// split across the group's lines, so the shares still add up to the export —
// and with the group rows shown, each line inside a group shows that same
// share, so its lines add up to its group to the cent (owner, 2026-10-22).
//
// NO # COLUMN (owner, 2026-10-22: "For now, let's just plainly hide the
// column. 'Line 3' gives you a general place to look, and the borders on the
// row lock it down visually."). Where it was, the expand column
// (`showsExpandColumn`) holds a group row's arrow and Expand All / Collapse
// All; every message that names a line by its number is unchanged.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type FocusEvent as ReactFocusEvent, type KeyboardEvent as ReactKeyboardEvent, type ReactElement } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Body1,
  Button,
  Checkbox,
  Input,
  Spinner,
  Switch,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import {
  AddRegular,
  ChevronDoubleDownRegular,
  ChevronDoubleUpRegular,
  DeleteRegular,
  ReOrderDotsVerticalRegular,
} from '@fluentui/react-icons'
import type { AssociatedEntity, Flag, Invoice, InvoiceLineItem, LineItemTransportationDetail } from '../lib/types'
import type { LineGroupOperation } from '../api/types'
import {
  checkAmounts,
  detailCategoryLabel,
  filterMoneyInput,
  formatCurrency,
  formatPrice,
  groupedMoney,
  groupedPrice,
  lineItemCodingLabel,
  priceText,
  sumKnownLineTotals,
  toNumber,
  type LineItemDetailScreen,
} from '../lib/format'
import {
  CHARGE_GROUP_NAME,
  additionalDetailSections,
  groupDisagrees,
  groupFieldViews,
  groupTagsDisagree,
  isGroupRow,
  realLines,
  type ChargeKind,
  type GroupField,
  type SourceSystem,
} from '@ap/shared'
import {
  associatedEntitiesState,
  groupEntitiesState,
  isBlank,
  lineDetailIconState,
  marketingCategoryEditableAt,
  additionalDetailState,
  showRequiredBorder,
} from '../lib/required'
import { fieldBorder, flaggedTooltip, lineFieldFlags, linesHaveUnresolvedFlag, type LineField } from '../lib/flagged'
import { TINT_PILLS, useLineMarkStyles, useMoneyInputStyles, useNumberInputStyles, useRequiredStyles, useSummaryLabelStyles } from '../lib/theme'
import { chargeSpread, distributeCharges, distributedGrandTotal } from '../lib/taxDistribution'
import { DotPill } from './Badges'
import { CODING_PICKER_PLACEHOLDER, EntityPicker, type EntityOption } from './EntityPicker'
import { FlagTooltip } from './FlagTooltip'
import { LockedValue } from './LockedValue'
import { AdditionalDetailPopover } from './AdditionalDetailPopover'
import { isCapitalEquipmentOnlyAccount, loadAssociatedEntities, searchDepartments, searchAccounts } from '../lib/data'
import { useHalfLabels } from '../lib/hooks'
import { capitalEquipmentAllowed } from '../lib/constants'
import { bulkApplyScope, sumTotals, useLineSelection, type BulkChoice, type CombineInput } from '../lib/lineItemBulk'
import { CHARGE_KINDS, chargePlaceholderKey, rowKey, type GroupState, type LineItemState } from '../lib/intakeSaveBody'
import { codingFocusOnEnter, codingFocusOnLeave, codingFocusToRestore, refusalFor, type CodingFocus, type CodingRefusal } from '../lib/pickerCommit'
import type { LineDetailState } from '../lib/invoiceEditorState'
import { LineItemBulkBar, type BulkSelectedLine } from './LineItemBulkBar'
import { LineGroupRow } from './LineGroupRow'
import { ErrorBanner } from './States'
import {
  EDITOR_WIDTHS,
  READ_ONLY_WIDTHS,
  editorLineTableMinWidth,
  readOnlyDetailsWidth,
  readOnlyLineTableMinWidth,
  showsExpandColumn,
} from '../lib/lineItemColumns'

const useStyles = makeStyles({
  panel: { padding: '2px 0' },
  none: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  // The table scrolls inside its own box rather than widening a narrow modal
  // (QA Q17).
  tableScroll: { overflowX: 'auto', maxWidth: '100%', minWidth: 0 },
  // Fixed layout: sized columns take their width and the text columns split
  // the rest; the inline `minWidth` (`lib/lineItemColumns.ts`) stops the text
  // columns being crushed (QA 2026-09-30, C1-01 and C2-02). Header labels clip
  // with an ellipsis rather than spilling into the next column.
  table: {
    tableLayout: 'fixed',
    width: '100%',
    fontVariantNumeric: 'tabular-nums',
    '& .fui-TableHeaderCell__button': {
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  // The read-only table's small uppercase headers.
  head: {
    '& .fui-TableHeaderCell__button': {
      fontSize: '10.5px',
      fontWeight: tokens.fontWeightSemibold,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: tokens.colorNeutralForeground3,
    },
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px', // matches SUMMARY_AMOUNT_PAD — see theme.ts's `amount`
    marginTop: '10px',
    fontWeight: tokens.fontWeightSemibold,
    fontVariantNumeric: 'tabular-nums',
  },
  num: { textAlign: 'right' },
  // A box fills its cell and may shrink inside a fixed-layout table: Fluent's
  // `Input` ships `min-width: 160px`, which would set the numeric columns' floor.
  cellInput: { width: '100%', minWidth: 0 },
  // Right-aligned numeric headers: `textAlign` on the cell is inert, because
  // the label lives in an inner `.fui-TableHeaderCell__button` (app/README.md).
  numHeader: {
    textAlign: 'right',
    '& .fui-TableHeaderCell__button': { justifyContent: 'flex-end', width: '100%' },
  },
  // The two revealed charge columns on a flat (unboxed) mount: dimmed, because
  // they are derived shares, not amounts anyone keyed.
  derivedNum: { textAlign: 'right', color: tokens.colorNeutralForeground3 },
  footerControls: { display: 'flex', alignItems: 'center', gap: '10px' },
  // The icons at the end of a row: Additional Detail, which holds the
  // Associated Entities too (D-425), and in the editor Delete (D-154).
  iconCell: { display: 'flex', alignItems: 'center', gap: '2px', justifyContent: 'flex-end' },
  // No `display` on the cell itself: Fluent's TableCell sets `height: 34px`,
  // a floor on a table cell but enforced on a flex item, so a flex cell clipped
  // its picker. The flex row is the inner block.
  codingCell: { minWidth: 0 },
  codingCellInner: { display: 'flex', alignItems: 'flex-start', gap: '2px', minWidth: 0 },
  pickerStack: { display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0, flexGrow: 1 },
  selectCell: { width: `${READ_ONLY_WIDTHS.select}px` },
  addRow: { display: 'flex', paddingTop: '4px' },
  // NO ROW FILL (D-408): every row keeps the table's background. From D-405
  // until D-407 the lines in a group were light blue, then group rows gray.
  // Show Group Rows sits at the footer's left end, the grouping controls
  // together; the rest stays at the right (owner, 2026-10-22).
  footerStart: { marginRight: 'auto', display: 'flex', alignItems: 'center' },
  // Show Group Rows while a hidden group blocks (D-409): the red every
  // required field takes, as a box around the switch and its label.
  toggleBlocked: {
    border: `1px solid ${tokens.colorPaletteRedBorder2}`,
    borderRadius: tokens.borderRadiusMedium,
    paddingRight: '4px',
  },
  // A line inside a group: its Description sits indented under the group's
  // (owner, 2026-10-23 — D-408: "indenting the Description field on a child
  // row ... by more than you had previously"), the one field that moves; the
  // rest keep their columns.
  // A LINE INSIDE A GROUP: its Description indented 44px (32px from D-408 until
  // the owner asked for "slightly ... more", 2026-10-28), with a short
  // rule across the indent from the handle to its box (owner, 2026-10-28 —
  // "a little horizontal line between the drag icon and the Description of a
  // child row").
  memberIndent: {
    paddingLeft: '44px',
    position: 'relative',
    '::before': {
      content: '""',
      position: 'absolute',
      // From just right of the handle, back in the expand column, to just
      // short of the Description box.
      left: '-28px',
      width: '68px',
      top: '50%',
      borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    },
  },
  // THE RULE BETWEEN THE THREE SECTIONS (owner, 2026-10-28 — D-424: "a
  // different kind of divider line between the group section, the high-level
  // line item section, and the Tax/Shipping section, perhaps with a little bit
  // of extra space around that divider line"): a dotted rule, unlike a row's
  // solid hairline, with a little room above and below it (owner, the same
  // day: "I was thinking more along the lines of a jagged or dotted line, and
  // probably about half of that padding would suffice"; a solid 2px rule with
  // 8px either side until then).
  // `height: 'auto'`: the table's cells take a 34px row height, which left
  // the rule floating mid-row, well below the headings (owner, 2026-10-28).
  // 16px either side of a rule between two sections (owner, 2026-10-28).
  sectionDividerCell: { padding: '16px 0', borderBottom: 'none', height: 'auto' },
  // The first rule sits right under the headings, where their hairline was,
  // and the last right under the final row: room only on the rows' side
  // (owner, 2026-10-28).
  firstRuleCell: { paddingTop: 0, paddingBottom: '16px' },
  lastRuleCell: { paddingTop: '16px', paddingBottom: 0 },
  sectionRule: { borderTop: `1px solid ${tokens.colorNeutralStroke2}` },
  noRowRule: { borderBottomStyle: 'none', ':hover': { borderBottomStyle: 'none' } },
  // A rule's own row never takes the hover tint: it is not a line.
  ruleRow: { ':hover': { backgroundColor: 'transparent' }, ':active': { backgroundColor: 'transparent' } },
  // A line that can be dragged onto a group or out of one (G-20).
  draggable: { cursor: 'grab' },
  // The drag handle, where a group row shows its arrow (D-406), in the shared
  // mark box (`useLineMarkStyles`, D-407); only the cursor is its own.
  dragHandle: { cursor: 'grab' },
  dropTarget: { outline: `2px dashed ${tokens.colorBrandStroke1}`, outlineOffset: '-2px' },
})

/**
 * THE IMPORT CONTROL EDITOR'S ROWS AND WRITES — the table's editable row
 * model. `EditableInvoiceForm` owns the state and the saves; the table draws
 * the rows and calls back. Field keys are `description:<id>`, `price:<id>` and
 * `quantity:<id>`, the editor's focus and save-on-blur keys.
 */
export interface LineItemEditor {
  rows: readonly LineItemState[]
  details: Readonly<Record<string, LineDetailState>>
  /** The stored lines by id, for the Associated Entities pop-up. */
  storedLines: ReadonlyMap<string, InvoiceLineItem>
  /** Every control locked: an invoice at a status this editor does not cover. */
  locked: boolean
  /** The invoice came from another system (`isLockedSource`, D-380): each
      line's Account and Department stay open, and nothing else is offered —
      no typing in a line, no adding, removing or combining lines. */
  sourceLocked: boolean
  /** The flags behind the yellow borders (D-248). */
  flags: readonly Flag[]
  /** Where the row icons and the bulk row mount their pop-ups. */
  popupMountNode: HTMLElement | null
  setDescription: (id: string, value: string) => void
  setAmounts: (id: string, amounts: { price?: string; quantity?: string }) => void
  /** A field gained focus, with its value then. */
  focusField: (key: string, value: string) => void
  /** A field lost focus, with its value now: the editor saves if it changed. */
  blurField: (key: string, value: string) => void
  /** Enter commits a field (owner item 9, 2026-08-28). */
  onFieldKeyDown: (e: ReactKeyboardEvent<HTMLInputElement>) => void
  pickAccount: (id: string, option: EntityOption) => void
  /** Empties the line's Account, and its Department with it (D-295). */
  clearAccount: (id: string) => void
  pickDepartment: (id: string, option: EntityOption) => void
  pickCategory: (id: string, option: EntityOption) => void
  removeRow: (id: string) => void
  /** Delete in the bulk row (D-407): every checked line, as its trash button would, in one save. */
  removeRows: (ids: ReadonlySet<string>) => void
  addRow: () => void
  applyToRows: (ids: ReadonlySet<string>, choice: BulkChoice) => void
  /** The groups on screen (D-399). */
  groups: readonly GroupState[]
  /** Group (named Combine until D-407) makes a group of the lines (G-20). */
  combine: (ids: ReadonlySet<string>, input: CombineInput) => void
  removeFromGroup: (ids: ReadonlySet<string>) => void
  moveToGroup: (ids: ReadonlySet<string>, groupKey: string) => void
  renameGroup: (groupKey: string, name: string) => void
  /** Removing a group row: its lines stand alone. */
  ungroup: (groupKey: string) => void
  /** A tag was written in the Associated Entities pop-up. */
  tagsChanged: () => void
}

/** One row, from either source. */
interface TableLine {
  id: string
  /** The row's React key and focus key: a new line keeps it when the save
      gives it its stored id, so the row is not remounted under the caret. */
  key: string
  number: number | null
  description: string
  accountId: string | null
  /** A label picked this session; the stored ids are labelled from the masters. */
  accountLabel?: string
  departmentId: string | null
  departmentLabel?: string
  /** Read: the stored price. Edit: the text in the box. */
  price: string | number | null
  quantity: string | number | null
  total: string | number | null
  marketing: { categoryId: string | null; label: string }
  /** Fleet Maintenance's own detail, read-only (D-381); `null` on every other line. */
  transportation: LineItemTransportationDetail | null
  /** The line's source system — which decides whether it shows that detail. */
  source: SourceSystem | null
  /** The stored line; a stub carrying the id for a line added this session. */
  item: InvoiceLineItem
  /** Stored on the server: what a category write and a tag need. */
  saved: boolean
  isNew: boolean
  /** An item, or a group row (D-396). */
  kind: 'item' | 'group'
  /** An item's group key, or a group row's own key; null on an item standing alone. */
  groupKey: string | null
  /** A group row's lines, and how its three fields read from them (G-8). */
  members?: TableLine[]
  views?: Record<GroupField, { value: string | null; red: boolean }>
  /** On a group row: its charge kind when it is the Tax or Shipping group (D-420). */
  chargeKind?: ChargeKind | null
  /** On a line: it sits in the Tax or Shipping group, so it carries no coding (D-420). */
  charge?: boolean
}

/** The yellow-border tooltip, on the editor's controls only: a read table
    draws no flag tooltips, and gains no wrapper around its pickers. */
function EditorTooltip({ editing, text, children }: { editing: boolean; text: string | undefined; children: ReactElement }) {
  return editing ? <FlagTooltip text={text}>{children}</FlagTooltip> : children
}

function lineFromStored(c: InvoiceLineItem): TableLine {
  const md = c.marketing_detail
  return {
    id: c.id,
    key: c.id,
    number: c.line_number ?? null,
    description: c.description ?? '—',
    accountId: c.account_id ?? null,
    departmentId: c.department_id ?? null,
    price: c.unit_price ?? null,
    quantity: c.quantity ?? null,
    total: c.total ?? null,
    marketing: {
      categoryId: md?.category_id ?? null,
      label: detailCategoryLabel(md?.category_id ?? null, md?.category ?? null, md?.category_context ?? null),
    },
    transportation: c.transportation_detail ?? null,
    source: c.source_system ?? null,
    item: c,
    saved: true,
    isNew: false,
    kind: 'item',
    groupKey: c.group_id ?? null,
  }
}

function lineFromEditor(
  r: LineItemState,
  d: LineDetailState | undefined,
  stored: InvoiceLineItem | undefined,
): TableLine {
  return {
    id: r.lineItemId,
    key: rowKey(r),
    number: r.sequence ?? null,
    description: r.description,
    accountId: r.accountId ?? null,
    accountLabel: r.accountLabel,
    departmentId: r.departmentId ?? null,
    departmentLabel: r.departmentLabel,
    price: r.price,
    quantity: r.quantity,
    total: r.total,
    marketing: {
      categoryId: d?.marketingCategoryId ?? null,
      label: detailCategoryLabel(d?.marketingCategoryId ?? null, d?.marketingCategory ?? null, d?.marketingCategoryContext ?? null),
    },
    transportation: d?.transportation ?? null,
    source: stored?.source_system ?? null,
    item: stored ?? ({ id: r.lineItemId } as InvoiceLineItem),
    // A line the server knows has a counter (`rowVersion`); one added this
    // session has none until the save that creates it answers.
    saved: typeof r.rowVersion === 'number',
    isNew: !!r.isNew,
    kind: 'item',
    groupKey: null,
  }
}

/**
 * A GROUP ROW, drawn from its lines (G-6, G-8): the value of each field when
 * every non-empty line agrees, red when one is empty or two disagree; the sum
 * of the lines' totals as its price and total; quantity 1.
 */
function groupLine(
  key: string,
  storedId: string | undefined,
  name: string,
  number: number | null,
  members: TableLine[],
  stored: InvoiceLineItem | undefined,
  chargeKind: ChargeKind | null = null,
): TableLine {
  // A CHARGE GROUP (D-420): its name fixed, its three fields blank and never red.
  const blank = { value: null, red: false }
  const views = chargeKind
    ? { account_id: blank, department_id: blank, marketing_category_id: blank }
    : groupFieldViews(
        members.map((m) => ({ account_id: m.accountId, department_id: m.departmentId, marketing_category_id: m.marketing.categoryId })),
      )
  if (chargeKind) name = CHARGE_GROUP_NAME[chargeKind]
  const total = sumTotals(members.map((m) => (m.total === '' ? null : m.total)))
  const marketingLabel = members.find((m) => m.marketing.categoryId === views.marketing_category_id.value)?.marketing.label ?? '—'
  return {
    id: storedId ?? key,
    key: `group:${key}`,
    number,
    description: name,
    accountId: views.account_id.value,
    accountLabel: members.find((m) => m.accountId === views.account_id.value)?.accountLabel,
    departmentId: views.department_id.value,
    departmentLabel: members.find((m) => m.departmentId === views.department_id.value)?.departmentLabel,
    price: total,
    quantity: '1',
    total,
    marketing: { categoryId: views.marketing_category_id.value, label: views.marketing_category_id.value ? marketingLabel : '—' },
    transportation: null,
    source: members[0]?.source ?? null,
    item: stored ?? ({ id: storedId ?? key } as InvoiceLineItem),
    saved: storedId !== undefined,
    isNew: storedId === undefined,
    kind: 'group',
    groupKey: key,
    members,
    views,
    chargeKind,
  }
}

/**
 * THE GROUPS AS THE TABLE SHOWS THEM (D-420): the ordinary groups that hold a
 * line, then the Tax group and the Shipping group — stored ones even when
 * empty, and an empty placeholder for a kind the invoice does not hold yet.
 */
function withChargeGroups(built: readonly TableLine[]): TableLine[] {
  const ordinary = built.filter((g) => !g.chargeKind && (g.members ?? []).length > 0)
  const charges = CHARGE_KINDS.map(
    (kind) =>
      built.find((g) => g.chargeKind === kind) ??
      groupLine(chargePlaceholderKey(kind), undefined, CHARGE_GROUP_NAME[kind], null, [], undefined, kind),
  )
  return [...ordinary, ...charges]
}

/** A charge group's lines (D-420): Account, Department and Marketing Category blank. */
function markChargeLines(lines: readonly TableLine[], keys: ReadonlySet<string>): TableLine[] {
  return lines.map((l) =>
    l.groupKey !== null && keys.has(l.groupKey)
      ? {
          ...l,
          charge: true,
          accountId: null,
          accountLabel: undefined,
          departmentId: null,
          departmentLabel: undefined,
          marketing: { categoryId: null, label: '—' },
        }
      : l,
  )
}

/** The position a row sorts at: a group at its first line's (finding 11). */
function position(l: TableLine): number {
  if (l.kind === 'group') return Math.min(...(l.members ?? []).map((m) => m.number ?? Number.MAX_SAFE_INTEGER))
  return l.number ?? Number.MAX_SAFE_INTEGER
}

interface Props {
  /** The stored lines (read mode). Ignored when `editor` is given. */
  lineItems?: InvoiceLineItem[] | 'loading'
  /** The Import Control editor's rows and writes (edit mode). */
  editor?: LineItemEditor
  /** Which screen mounted this table: which Additional Detail a line shows
      and which empty required fields are red here (`../lib/required.ts`). */
  screen: LineItemDetailScreen
  /** The invoice: what the Associated Entities pop-up reads its tags against,
      and, without `entitiesByLineItem`, what this table loads them for. */
  invoice?: Invoice
  /** The invoice amounts the footer reconciles against and distributes. */
  subtotal?: number | string | null
  tax?: number | string | null
  shipping?: number | string | null
  total?: number | string | null
  /** The tags behind each line's Associated Entities icon (D-154), by line id.
      Absent, this table loads them itself for `invoice`. */
  entitiesByLineItem?: Record<string, AssociatedEntity[]>
  /** Read mode: the Account column is a picker, the account half-write. */
  onAccountChange?: (lineItem: InvoiceLineItem, accountId: string) => void
  /** Read mode: the Department column is a picker, the department half-write.
      Its options derive from the line's own account. */
  onDepartmentChange?: (lineItem: InvoiceLineItem, departmentId: string) => void
  /** Read mode: THE LINE-ITEM BULK BAR (D-228) — a box per line, Select All
      in the header, and the bulk row above the table (D-242): an Account
      (required) and a Department (optional) applied to every checked line.
      Where `onDepartmentChange` is passed and `onAccountChange` is not, the
      Account is locked by the route (D-305) and the row applies the
      Department alone (`bulkApplyScope`). With neither, no boxes render. The
      caller writes each line and throws a message on a refusal. No Combine:
      only Import Control adds and removes lines. */
  onBulkApply?: (lineItems: InvoiceLineItem[], choice: BulkChoice) => Promise<void>
  /** Read mode: a line's Additional Detail pop-up offers the Marketing
      Category picker where it may be written here (`categoryPickableAt`, D-192, D-216). */
  onCategoryChange?: (lineItem: InvoiceLineItem, categoryId: string) => void
  /** Read mode: the line whose coding write is in flight — its pickers wait. */
  savingLineItemId?: string
  /** Read mode: bumped by the caller when a coding write was REFUSED, to
      remount the pickers so each re-seeds from the stored value. A refused
      pick leaves the value unchanged, which is the one case `EntityPicker`'s
      own resync cannot see (QA walk, 2026-08-30). */
  pickerResetNonce?: number
  /** Read mode: the caller's message for a refused coding write, by the line
      and the control it was refused on (`'Account'` or `'Department'`). That
      picker names the message as its description (`aria-describedby`), so a
      screen reader reads the reason with the field (QA 2026-10-04, C1-09). */
  refusal?: CodingRefusal
  /** Read mode, with no coding write: `'code'` shows the composed Code
      column; `'halves'` shows Account and Department read-only;
      `'account-only'` shows Account alone. */
  codingDisplay?: 'code' | 'halves' | 'account-only'
  /** Read mode: draw every locked value as a shaded box (`LockedValue`). By
      default boxed exactly when the table offers a coding control; the modal
      passes `true` so its fully read-only steps match the header panel. */
  boxedFields?: boolean
  /** Read mode: where the pickers mount their listboxes, for a caller that is
      not inside a Dialog (the expanded queue card, owner item 22). Unset in a
      dialog, so `EntityPicker` resolves its own host. */
  pickerMountNode?: HTMLElement | null
  /** Read mode, where grouping may change here (`groupingOpen`, G-12): one
      change to the invoice's groups (`POST /invoices/{id}/line-groups`). The
      caller throws a message on a refusal. Absent, groups are view-only. */
  onGroupOperation?: (operation: LineGroupOperation) => Promise<void>
  /** A group's name may be typed here; the queue card passes `false` (D-409). */
  groupNamesEditable?: boolean
  /** THE VENDOR WHOSE GROUPS THE NAME PICKERS OFFER (owner, 2026-10-26 —
      D-418: "Just change the Description suggestions/dropdowns offered to
      match the newly chosen vendor"). The editor passes its header Vendor as
      it stands, an unsaved pick included; nothing is regrouped. Absent, the
      invoice's stored vendor. */
  groupVendorId?: string | null
}

export function InvoiceLineItemsTable({
  lineItems,
  editor,
  screen,
  invoice,
  subtotal,
  tax,
  shipping,
  total,
  entitiesByLineItem: entitiesProp,
  onAccountChange,
  onDepartmentChange,
  onBulkApply,
  onCategoryChange,
  savingLineItemId,
  pickerResetNonce = 0,
  refusal,
  codingDisplay = 'code',
  boxedFields: boxedFieldsProp,
  pickerMountNode,
  onGroupOperation,
  groupNamesEditable = true,
  groupVendorId: groupVendorIdProp,
}: Props) {
  const s = useStyles()
  const ms = useMoneyInputStyles()
  const ns = useNumberInputStyles()
  const rs = useRequiredStyles()
  const marks = useLineMarkStyles()
  const labels = useHalfLabels()
  // D-418: the Vendor whose groups the name pickers offer — the editor's header
  // Vendor when it passes one, else the stored invoice's.
  const groupVendorId = groupVendorIdProp === undefined ? (invoice?.vendor_id ?? null) : groupVendorIdProp
  // A REFUSED SAVE KEEPS YOUR PLACE (QA 2026-10-04, review of C2-06). A refusal
  // remounts the row's pickers (`pickerResetNonce`) so each re-seeds from its
  // stored value, and a remounted box loses the focus the old one had: Tab from
  // an Account the server refused (Capital Equipment under the floor) left focus
  // on the page. The last focused coding picker is remembered by its line and
  // name, and focus goes back to its new copy when the remount dropped it. A
  // click on blank space forgets it, so a refusal that lands after the reviewer
  // left on purpose does not pull focus back (`lib/pickerCommit.ts`).
  const tableRef = useRef<HTMLTableElement>(null)
  const lastCodingFocus = useRef<CodingFocus | null>(null)
  function rememberCodingFocus(e: ReactFocusEvent<HTMLTableElement>) {
    lastCodingFocus.current = codingFocusOnEnter(
      e.target.getAttribute('aria-label'),
      e.target.closest('tr')?.getAttribute('data-line-key'),
    )
  }
  function forgetCodingFocusOnDeparture(e: ReactFocusEvent<HTMLTableElement>) {
    lastCodingFocus.current = codingFocusOnLeave(lastCodingFocus.current, {
      relatedTarget: e.relatedTarget,
      targetConnected: e.target.isConnected,
    })
  }
  useLayoutEffect(() => {
    const active = document.activeElement
    const last = codingFocusToRestore(lastCodingFocus.current, {
      pickerResetNonce,
      focusOnNothing: !active || active === document.body,
    })
    if (!last) return
    const rows = tableRef.current?.querySelectorAll<HTMLTableRowElement>('tr[data-line-key]') ?? []
    const row = [...rows].find((r) => r.getAttribute('data-line-key') === last.line)
    row?.querySelector<HTMLInputElement>(`input[aria-label="${last.name}"]`)?.focus()
  }, [pickerResetNonce])
  const editing = editor !== undefined
  const locked = editor?.locked ?? false
  // A LOCKED SOURCE (D-380) freezes every line value but the two coding halves:
  // Description, Price and Quantity are read-only, and no line is added,
  // removed or combined. The server refuses the rest; this only stops offering it.
  const sourceLocked = editor?.sourceLocked ?? false
  const lineValuesLocked = locked || sourceLocked

  // EVERY HOOK RUNS BEFORE THE EARLY RETURNS below.
  //
  // THE REAL LINES (`lines`) and THE GROUPS over them (`groups`). The editor
  // holds its rows and its groups apart; a stored set carries the group rows
  // among its lines (D-396).
  const { lines, groups } = useMemo<{ lines: TableLine[]; groups: TableLine[] }>(() => {
    if (editor) {
      const items = editor.rows.map((r) => lineFromEditor(r, editor.details[r.lineItemId], editor.storedLines.get(r.lineItemId)))
      const keyOf = new Map<string, string>()
      for (const g of editor.groups) for (const m of g.members) keyOf.set(m, g.key)
      const withGroups = items.map((l) => ({ ...l, groupKey: keyOf.get(l.id) ?? null }))
      // The charge lines are marked first, so a charge group's row reads blank from them.
      const marked = markChargeLines(withGroups, new Set(editor.groups.filter((g) => g.chargeKind).map((g) => g.key)))
      const built = editor.groups.map((g) =>
        groupLine(
          g.key,
          g.storedId,
          g.name,
          g.sequence ?? null,
          marked.filter((l) => l.groupKey === g.key),
          g.storedId ? editor.storedLines.get(g.storedId) : undefined,
          g.chargeKind ?? null,
        ),
      )
      return { lines: marked, groups: withChargeGroups(built) }
    }
    if (!Array.isArray(lineItems)) return { lines: [], groups: [] }
    const stored = realLines(lineItems).map(lineFromStored)
    const groupRows = lineItems.filter((c) => c.is_active && isGroupRow(c))
    const items = markChargeLines(stored, new Set(groupRows.filter((g) => g.charge_kind).map((g) => g.id)))
    const built = groupRows.map((g) =>
      groupLine(g.id, g.id, g.description ?? '', g.line_number ?? null, items.filter((l) => l.groupKey === g.id), g, g.charge_kind ?? null),
    )
    return { lines: items, groups: withChargeGroups(built) }
  }, [editor, lineItems])

  // COLLAPSED BY DEFAULT (G-19): the groups open here, by key. A new invoice
  // starts with every group closed.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set())
  // The groups that can open: an empty Tax or Shipping group has nothing to show (D-420).
  const openable = useMemo(() => groups.filter((g) => (g.members ?? []).length > 0), [groups])
  const allExpanded = openable.length > 0 && openable.every((g) => expanded.has(g.groupKey as string))
  const toggleGroup = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  // WHAT IS ON SCREEN, IN ORDER: the groups first, by their first line's
  // position, then the standalone lines by theirs (owner, 2026-10-27,
  // replacing finding 11's interleaving), then the Tax group and the Shipping
  // group, last (D-420), an open group's lines straight after it — or, with
  // Show Group Rows off, every real line by its number, charge lines included.
  const [showGroupRows, setShowGroupRows] = useState(true)
  const shown = useMemo<TableLine[]>(() => {
    if (!showGroupRows) return [...lines].sort((a, b) => (a.number ?? 0) - (b.number ?? 0))
    const byPosition = (a: TableLine, b: TableLine) => position(a) - position(b)
    const ordinary = groups.filter((g) => !g.chargeKind)
    const charges = groups.filter((g) => g.chargeKind)
    const top = [...[...ordinary].sort(byPosition), ...lines.filter((l) => l.groupKey === null).sort(byPosition), ...charges]
    const out: TableLine[] = []
    for (const l of top) {
      out.push(l)
      if (l.kind === 'group' && expanded.has(l.groupKey as string)) {
        out.push(...[...(l.members ?? [])].sort((a, b) => (a.number ?? 0) - (b.number ?? 0)))
      }
    }
    return out
  }, [lines, groups, expanded, showGroupRows])
  // The lines that may be checked: every real line, never a group row — a
  // collapsed group's lines too, so its group box can check them (D-406).
  const checkable = lines

  // THE TAGS BEHIND EACH LINE'S ASSOCIATED ENTITIES ICON (D-154). The modal and
  // the editor pass them; the queue card does not, so the table reads them
  // itself once its lines have loaded — a collapsed card mounts this table with
  // no lines, and a queue of them must not fire one read each.
  const [loadedEntities, setLoadedEntities] = useState<Record<string, AssociatedEntity[]>>({})
  const invoiceId = invoice?.id
  useEffect(() => setExpanded(new Set()), [invoiceId])
  const linesLoaded = lines.length > 0
  useEffect(() => {
    if (entitiesProp !== undefined || !invoiceId || !linesLoaded) return
    let live = true
    loadAssociatedEntities(invoiceId)
      .then((rows) => {
        if (!live) return
        const grouped: Record<string, AssociatedEntity[]> = {}
        for (const e of rows) if (e.child_id) (grouped[e.child_id] ??= []).push(e)
        setLoadedEntities(grouped)
      })
      .catch(() => { if (live) setLoadedEntities({}) })
    return () => { live = false }
  }, [entitiesProp, invoiceId, linesLoaded])
  const entitiesByLineItem = entitiesProp ?? loadedEntities

  // The bulk bar's selection (D-228). It resets when another invoice opens and
  // drops a line the rows no longer hold.
  const selection = useLineSelection(useMemo(() => checkable.map((l) => l.id), [checkable]), invoiceId)
  const selected = useMemo(() => checkable.filter((l) => selection.selectedIds.has(l.id)), [checkable, selection.selectedIds])
  // THE GROUP A LINE IS DRAGGED FROM, and the row it is over (G-20).
  const [dragging, setDragging] = useState<string | null>(null)
  const [dropOver, setDropOver] = useState<string | null>(null)
  // The line in flight, for the page-wide drop below, which must read it
  // without waiting for a render.
  const draggingRef = useRef<string | null>(null)
  const [applying, setApplying] = useState(false)
  const [bulkError, setBulkError] = useState<string | null>(null)
  useEffect(() => setBulkError(null), [invoiceId])

  // THE CAPITAL EQUIPMENT FLOOR'S TOTAL (D-403, review finding 3): a line in a
  // group is judged by its group's total, as the server judges it
  // (`floorTotalFor`); a line standing alone by its own.
  const groupTotals = useMemo(() => new Map(groups.map((g) => [g.groupKey as string, toNumber(g.total)])), [groups])
  const floorTotalOf = (l: TableLine): number =>
    l.groupKey !== null ? (groupTotals.get(l.groupKey) ?? toNumber(l.total)) : toNumber(l.total)

  // ITEM 14(b) — the accounts on this table, under the $5,000 floor, whose only
  // department is Capital Equipment (`190110`): their Department picker has
  // nothing to offer. Looked up once per distinct account, at the top level,
  // because the lookup is async and a row cannot hold a hook.
  const capitalCandidateAccounts = useMemo(
    () =>
      Array.from(
        new Set(
          lines
            .filter((l) => !!l.accountId && !capitalEquipmentAllowed(floorTotalOf(l)))
            .map((l) => l.accountId as string),
        ),
      ),
    [lines, groupTotals], // eslint-disable-line react-hooks/exhaustive-deps -- floorTotalOf reads groupTotals
  )
  const capitalCandidateKey = capitalCandidateAccounts.join('|')
  const [capitalOnlyAccounts, setCapitalOnlyAccounts] = useState<ReadonlySet<string>>(() => new Set())
  useEffect(() => {
    let cancelled = false
    Promise.all(
      capitalCandidateAccounts.map((accountId) =>
        isCapitalEquipmentOnlyAccount(accountId).then((isCapitalOnly) => [accountId, isCapitalOnly] as const),
      ),
    ).then((results) => {
      if (cancelled) return
      setCapitalOnlyAccounts(new Set(results.filter(([, only]) => only).map(([accountId]) => accountId)))
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- capitalCandidateAccounts is recomputed from capitalCandidateKey
  }, [capitalCandidateKey])

  // The footer toggle. A view preference: never sent, OFF on every mount, and
  // independent of the GL Summary's own (owner, 2026-08-14).
  const [distribute, setDistribute] = useState(false)
  // Show Group Rows (`showGroupRows`, above, owner 2026-10-22): the same kind
  // of view preference — on at every mount, never sent.
  const nested = showGroupRows
  // Which price boxes have focus: a focused box shows the raw text being typed,
  // a blurred one the comma-grouped price (owner item 12, 2026-08-18). State
  // never holds a comma.
  const [editingPrice, setEditingPrice] = useState<ReadonlySet<string>>(() => new Set())

  const applyBulk = useCallback(
    async (choice: BulkChoice) => {
      if (selected.length === 0) return
      // A charge line takes no Account or Department (D-420): Update passes it by.
      const targets = selected.filter((l) => !l.charge)
      setBulkError(null)
      setApplying(true)
      try {
        if (targets.length > 0) {
          if (editor) editor.applyToRows(new Set(targets.map((l) => l.id)), choice)
          else if (onBulkApply) await onBulkApply(targets.map((l) => l.item), choice)
        }
        // The boxes clear once the choice has landed, so a second Apply is a
        // new selection, not a repeat.
        selection.clear()
      } catch (e) {
        setBulkError(e instanceof Error ? e.message : String(e))
      } finally {
        setApplying(false)
      }
    },
    [editor, onBulkApply, selected, selection],
  )
  // THE GROUP CHANGES (D-399), for the bulk row, a group row's own controls and
  // a drag: the editor's state at Import Control and Denial Review, the
  // `line-groups` route past them. A refusal shows in the bulk row's text, or
  // above the table when no line is checked, and the group row's pickers go
  // back to what is stored (`groupResets`).
  const runGroup = useCallback(
    async (inEditor: () => void, operation: LineGroupOperation) => {
      setBulkError(null)
      try {
        if (editor) inEditor()
        else if (onGroupOperation) {
          setApplying(true)
          await onGroupOperation(operation)
        }
      } catch (e) {
        setBulkError(e instanceof Error ? e.message : String(e))
      } finally {
        setApplying(false)
      }
    },
    [editor, onGroupOperation],
  )
  const groupIdOf = (key: string) => groups.find((g) => g.groupKey === key)?.id ?? key
  // A MOVE ONTO A GROUP (G-20): by its id, or onto the Tax or Shipping group
  // by its kind (D-420), which makes the group when the invoice has none.
  const moveOperation = (key: string, ids: readonly string[]): LineGroupOperation => {
    const lines = ids.map((id) => ({ id }))
    const chargeKind = groups.find((g) => g.groupKey === key)?.chargeKind
    return chargeKind ? { op: 'move', charge_kind: chargeKind, lines } : { op: 'move', group_id: groupIdOf(key), lines }
  }
  const combineSelected = useCallback(
    async (input: CombineInput) => {
      const ids = new Set(selected.map((l) => l.id))
      const values = {
        ...(input.choice ? { account_id: input.choice.accountId } : {}),
        ...(input.choice?.departmentId ? { department_id: input.choice.departmentId } : {}),
        ...(input.marketing ? { marketing_category_id: input.marketing.id } : {}),
      }
      await runGroup(() => editor?.combine(ids, input), {
        op: 'create',
        name: input.description.trim(),
        lines: [...ids].map((id) => ({ id })),
        ...(Object.keys(values).length > 0 ? { values } : {}),
      })
      selection.clear()
    },
    [editor, selected, selection, runGroup],
  )
  const removeSelectedFromGroups = useCallback(async () => {
    const ids = new Set(selected.filter((l) => l.groupKey !== null).map((l) => l.id))
    await runGroup(() => editor?.removeFromGroup(ids), { op: 'remove', lines: [...ids].map((id) => ({ id })) })
    selection.clear()
  }, [editor, selected, selection, runGroup])
  const moveSelectedToGroup = useCallback(
    async (groupKey: string) => {
      const ids = new Set(selected.map((l) => l.id))
      await runGroup(() => editor?.moveToGroup(ids, groupKey), moveOperation(groupKey, [...ids]))
      selection.clear()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- moveOperation reads `groups`
    [editor, selected, selection, runGroup, groups],
  )
  // DELETE (owner, 2026-10-23 — D-407: "We need a Delete option when rows are
  // selected, too"): every checked line removed as its own trash button
  // removes it — a soft delete, its flags resolved (D-261), its corrections —
  // in one save. A group left with one line dissolves and one left with none
  // is retired (G-21). Offered only where the trash button is (the editor,
  // never a locked source, D-380); no confirmation, as the trash button has none.
  const deleteSelected = useCallback(async () => {
    if (!editor || selected.length === 0) return
    setBulkError(null)
    try {
      editor.removeRows(new Set(selected.map((l) => l.id)))
      selection.clear()
    } catch (e) {
      setBulkError(e instanceof Error ? e.message : String(e))
    }
  }, [editor, selected, selection])

  // A LINE DROPPED OFF THE ROWS LEAVES ITS GROUP (owner, 2026-10-23 — D-407:
  // "If I drag an item from within a group to elsewhere on the page (besides
  // another group), I would like it to separate from the group"). While a line
  // is dragged, the page accepts the drop: a drop no row handled (the rows
  // call `preventDefault`) takes a line in a group out of it, the path Remove
  // From Group takes (G-21 dissolves a group left with one line). A line
  // standing alone dropped there stays as it is, and a cancelled drag (Escape,
  // or a drop outside the window) fires no drop, so nothing changes. Only
  // where grouping may change, with the group rows shown.
  const groupingHere = editor ? !editor.locked : onGroupOperation !== undefined
  useEffect(() => {
    if (dragging === null || !groupingHere || !showGroupRows) return
    const over = (e: DragEvent) => e.preventDefault()
    const drop = (e: DragEvent) => {
      const id = draggingRef.current
      draggingRef.current = null
      setDragging(null)
      setDropOver(null)
      if (e.defaultPrevented || id === null) return
      e.preventDefault()
      const from = lines.find((x) => x.id === id)
      if (!from || from.groupKey === null) return
      void runGroup(() => editor?.removeFromGroup(new Set([id])), { op: 'remove', lines: [{ id }] })
    }
    document.addEventListener('dragover', over)
    document.addEventListener('drop', drop)
    return () => {
      document.removeEventListener('dragover', over)
      document.removeEventListener('drop', drop)
    }
  }, [dragging, groupingHere, showGroupRows, lines, editor, runGroup])

  if (!editing && lineItems === undefined) {
    return <div className={s.panel}><Spinner size="tiny" label="Loading line items…" /></div>
  }
  if (!editing && lineItems === 'loading') {
    return <div className={s.panel}><Spinner size="tiny" label="Loading line items…" /></div>
  }
  if (!editing && lines.length === 0 && groups.length === 0) {
    return <div className={s.panel}><Body1 className={s.none}>No line items on this invoice.</Body1></div>
  }

  // WHICH COLUMNS. The editor shows Account and Department as pickers. A read
  // table shows each half as a picker where its write is passed, and otherwise
  // as `codingDisplay` asks — the composed Code, both halves, or Account alone.
  const editsAccount = editing || onAccountChange !== undefined
  const editsDepartment = editing || onDepartmentChange !== undefined
  const readOnlyCoding = !editsAccount && !editsDepartment
  const boxedFields = editing || (boxedFieldsProp ?? !readOnlyCoding)
  const showCode = readOnlyCoding && codingDisplay === 'code'
  const showAccount = !readOnlyCoding || codingDisplay !== 'code'
  const showDepartment = editsDepartment || (readOnlyCoding && codingDisplay === 'halves')
  // THE SELECT COLUMN renders only where the bar can act: the editor's rows
  // unless frozen, and a read table's where a coding half is open — the
  // Account (with the Department), or the Department alone (D-305). A hold
  // changes nothing here (D-282).
  const bulkScope = bulkApplyScope({ account: editsAccount, department: editsDepartment })
  const bulk = editing ? !locked : onBulkApply !== undefined && bulkScope !== null
  // WHERE GROUPING MAY CHANGE (G-12): the editor unless frozen — a locked
  // source included (owner, 2026-10-22: "Allow grouping for fleet maintenance
  // rows") — and a read table given the group route.
  const groupingLive = editing ? !locked : onGroupOperation !== undefined
  const canPickCategories = editing || onCategoryChange !== undefined

  // The editor's columns are wider (inputs, two icons); the read table's
  // headers are small capitals. Every other ruling is shared.
  const widths = editing
    ? { select: EDITOR_WIDTHS.select, expand: EDITOR_WIDTHS.expand, account: EDITOR_WIDTHS.account, department: EDITOR_WIDTHS.department, price: EDITOR_WIDTHS.price, quantity: EDITOR_WIDTHS.quantity, tax: EDITOR_WIDTHS.tax, shipping: EDITOR_WIDTHS.shipping, total: EDITOR_WIDTHS.total, icons: EDITOR_WIDTHS.actions }
    : { select: READ_ONLY_WIDTHS.select, expand: READ_ONLY_WIDTHS.expand, account: undefined, department: undefined, price: READ_ONLY_WIDTHS.price, quantity: READ_ONLY_WIDTHS.quantity, tax: READ_ONLY_WIDTHS.tax, shipping: READ_ONLY_WIDTHS.shipping, total: READ_ONLY_WIDTHS.total, icons: readOnlyDetailsWidth(bulk) }
  const head = editing ? undefined : s.head
  const numHead = mergeClasses(head, s.numHeader)
  // THE EXPAND COLUMN, where # was (owner, 2026-10-22) — there while the
  // group rows show, WHETHER OR NOT ANY GROUP EXISTS YET (owner, 2026-10-23 —
  // D-410: "show these things, even if there are no groups in place"), so the
  // drag handles that make the first group are there to drag.
  const expandColumn = showsExpandColumn({ select: bulk, groupRows: nested })

  // THE TOGGLE, gated to known amounts: with none there is nothing to spread.
  // The editor's boxes are text, blank when cleared, and its Subtotal is always
  // derived, so there the three charges decide; a read table has the stored
  // values, null when unknown.
  const filled = (v: number | string | null | undefined) => v != null && String(v).trim() !== ''
  const amountsKnown = editing
    ? filled(tax) || filled(shipping) || filled(total)
    : subtotal != null || tax != null || total != null || shipping != null
  const taxDollars = toNumber(tax)
  const shippingDollars = toNumber(shipping)
  const printedTotal = total === undefined || total === null || String(total).trim() === '' ? null : toNumber(total)
  const distributing = amountsKnown && distribute
  // WHAT IS SPREAD (D-420): the header Tax and Shipping, plus the lines in the
  // Tax and Shipping groups — shared `spreadCharges`, the CSV's own figures.
  const spread = distributing
    ? chargeSpread(taxDollars, shippingDollars, [
        ...groups.map((g) => ({ id: g.groupKey as string, is_active: true, line_kind: 'group' as const, group_id: null, charge_kind: g.chargeKind ?? null })),
        ...lines.map((l) => ({ id: l.key, is_active: true, line_kind: 'item' as const, group_id: l.groupKey, total: l.total })),
      ])
    : { tax: 0, shipping: 0 }
  // THE CODED LINES' SUM: what the charges spread over, and with them what the
  // distributed rows add up to (D-420).
  const codedSum = lines.filter((l) => !l.charge).reduce((sum, l) => sum + toNumber(l.total), 0)
  // One allocation per charge across the LINES — the CSV's own grain (D-40) —
  // with the vendor's variance of up to three cents on the largest (D-151).
  const shares = new Map<string, { tax: number; shipping: number; total: number }>()
  if (distributing) {
    // EXPORTED ROWS (D-403): a group row in place of its lines, as the CSV. A
    // charge group is not one, and its lines take no share (D-420).
    const exported = [...lines.filter((l) => l.groupKey === null), ...groups.filter((g) => !g.chargeKind)]
    for (const d of distributeCharges(
      exported.map((l) => ({ id: l.key, total: toNumber(l.total), sequence: position(l) })),
      spread.tax,
      spread.shipping,
      printedTotal,
    )) {
      shares.set(String(d.id), { tax: d.tax, shipping: d.shipping, total: d.total })
    }
    // EACH GROUPED LINE shows its share of its group's, split by the same
    // rule, so a group's lines add up to the group and to the export — with
    // the group rows hidden, and under their group row when shown (owner,
    // 2026-10-22: the lines inside a group showed no Tax or Shipping).
    for (const g of groups) {
      if (g.chargeKind) continue
      const share = shares.get(g.key)
      if (!share) continue
      for (const d of distributeCharges(
        (g.members ?? []).map((m) => ({ id: m.key, total: toNumber(m.total), sequence: m.number ?? 0 })),
        share.tax,
        share.shipping,
        share.total,
      )) {
        shares.set(String(d.id), { tax: d.tax, shipping: d.shipping, total: d.total })
      }
    }
  }

  // THE HEADER ROW, once, so the bulk box above the table shows the very same
  // headers and the two cannot drift (D-242). The box's copy has no select-all
  // box and no expand column: one header spans both, "Action", over its picker
  // of Update, Group and the rest, which shows on every screen (owner, 2026-10-14).
  // Lines can be checked wherever the bulk row shows, so the expand column is
  // always there to span (`showsExpandColumn`).
  const lineHeader = (selectAll: boolean) => (
    <TableHeader>
      <TableRow className={selectAll && ruled ? s.noRowRule : undefined}>
        {bulk && !selectAll && (
          <TableHeaderCell colSpan={2} className={head} style={{ width: `calc(${widths.select + widths.expand}px + 2 * ${tokens.spacingHorizontalS})` }}>
            Action
          </TableHeaderCell>
        )}
        {bulk && selectAll && (
          <TableHeaderCell className={mergeClasses(head, s.selectCell)} style={{ width: widths.select }}>
            <Checkbox
              aria-label="Select every line item"
              checked={selection.headerState}
              disabled={applying}
              onChange={(_e, d) => selection.setAll(d.checked === true)}
            />
          </TableHeaderCell>
        )}
        {(selectAll || !bulk) && expandColumn && (
          <TableHeaderCell style={{ width: widths.expand }} className={head} aria-label="Expand or collapse groups">
            {/* EXPAND ALL / COLLAPSE ALL (G-19; owner, 2026-10-22): over the
                group rows' arrows, with the queues' own icons and words. */}
            {selectAll && nested && (
              <Button
                className={marks.mark}
                appearance="transparent"
                // Shown with no group to open, disabled (D-410).
                disabled={openable.length === 0}
                size="small"
                icon={allExpanded ? <ChevronDoubleUpRegular /> : <ChevronDoubleDownRegular />}
                aria-label={allExpanded ? 'Collapse All' : 'Expand All'}
                title={allExpanded ? 'Collapse All' : 'Expand All'}
                onClick={() => setExpanded(allExpanded ? new Set() : new Set(openable.map((g) => g.groupKey as string)))}
              />
            )}
          </TableHeaderCell>
        )}
        <TableHeaderCell className={head}>Description</TableHeaderCell>
        {/* "Code" NAMES A WHOLE CODE, read-only, where a surface asks for it;
            every coding surface edits the two halves as their own columns, and
            "Code" never collides with the "Account" half (D-35, D-43, D-139). */}
        {showCode && <TableHeaderCell className={head}>Code</TableHeaderCell>}
        {showAccount && <TableHeaderCell className={head} style={{ width: widths.account }}>Account</TableHeaderCell>}
        {showDepartment && <TableHeaderCell className={head} style={{ width: widths.department }}>Department</TableHeaderCell>}
        <TableHeaderCell style={{ width: widths.price }} className={numHead}>Price</TableHeaderCell>
        <TableHeaderCell style={{ width: widths.quantity }} className={numHead}>Quantity</TableHeaderCell>
        {/* The two DERIVED charge columns, between Quantity and Total, the
            order the arithmetic reads. */}
        {distributing && <TableHeaderCell style={{ width: widths.tax }} className={numHead}>Tax</TableHeaderCell>}
        {distributing && <TableHeaderCell style={{ width: widths.shipping }} className={numHead}>Shipping</TableHeaderCell>}
        <TableHeaderCell style={{ width: widths.total }} className={numHead}>Total</TableHeaderCell>
        <TableHeaderCell style={{ width: widths.icons }} className={head} aria-label={editing ? 'Line actions' : 'Line details'} />
      </TableRow>
    </TableHeader>
  )
  const lineTableMinWidth = editing
    ? editorLineTableMinWidth({ select: bulk, expand: expandColumn, distributing })
    : readOnlyLineTableMinWidth({ select: bulk, expand: expandColumn, code: showCode, account: showAccount, department: showDepartment, distributing })
  const popupMountNode = editor ? editor.popupMountNode : pickerMountNode

  /** A line control's border: red when required and empty, else yellow when
      an unresolved flag is about it (D-248). The editor's alone: the flags are
      resolved by the time an invoice reaches any other step. */
  const lineBorder = (red: boolean, l: TableLine, field: LineField) => {
    if (!editor) return undefined
    const border = fieldBorder(red, lineFieldFlags(editor.flags, l.number, field))
    return border === 'required' ? rs.requiredEmpty : border === 'flagged' ? rs.flagged : undefined
  }
  const lineTip = (red: boolean, l: TableLine, field: LineField) =>
    editor && !red ? flaggedTooltip(lineFieldFlags(editor.flags, l.number, field)) : undefined

  const bulkSelected: BulkSelectedLine[] = selected.map((l) => ({
    id: l.id,
    total: l.total,
    floorTotal: floorTotalOf(l),
    accountId: l.accountId,
    departmentId: l.departmentId,
    marketingCategoryId: l.marketing.categoryId,
    marketingLabel: l.marketing.label,
    groupKey: l.groupKey,
    saved: l.saved,
  }))

  // A DRAG (G-20, restated by D-406): a line onto a group row or one of its
  // lines joins that group, leaving its own; onto another line standing alone,
  // the two make a NEW group with an empty, required Description. Onto itself
  // or its own group, nothing. Each line keeps its values.
  const dropTargetGroup = (target: TableLine): string | null => target.groupKey
  const dragProps = (l: TableLine) => {
    // No drag while the group rows are hidden (owner, 2026-10-22).
    if (!groupingLive || !nested) return {}
    const props: Record<string, unknown> = {
      onDragOver: (e: React.DragEvent) => {
        if (dragging === null) return
        e.preventDefault()
        setDropOver(l.key)
      },
      onDragLeave: () => setDropOver((prev) => (prev === l.key ? null : prev)),
      onDrop: (e: React.DragEvent) => {
        // Handled here: the page-wide drop (D-407) sees `defaultPrevented` and
        // leaves it alone.
        e.preventDefault()
        const id = draggingRef.current ?? dragging
        draggingRef.current = null
        setDragging(null)
        setDropOver(null)
        if (id === null) return
        const from = lines.find((x) => x.id === id)
        if (!from || from.id === l.id) return
        const to = dropTargetGroup(l)
        if (to !== null) {
          if (from.groupKey === to) return
          void runGroup(() => editor?.moveToGroup(new Set([id]), to), moveOperation(to, [id]))
          return
        }
        // Two lines standing alone (the dragged one may leave a group): a new
        // group, named later.
        const ids = new Set([id, l.id])
        void runGroup(() => editor?.combine(ids, { description: '' }), { op: 'create', name: '', lines: [{ id }, { id: l.id }] })
      },
    }
    if (l.kind === 'item') {
      props.draggable = true
      props.onDragStart = (e: React.DragEvent) => {
        e.dataTransfer.setData('text/plain', l.id)
        e.dataTransfer.effectAllowed = 'move'
        draggingRef.current = l.id
        setDragging(l.id)
      }
      props.onDragEnd = () => {
        draggingRef.current = null
        setDragging(null)
        setDropOver(null)
      }
    }
    return props
  }

  // THE ROWS DRAWN, and the rule before each section (D-424). With View With
  // Tax & Shipping Distributed on, the Tax and Shipping rows and their lines
  // are hidden: their amounts are spread over the other lines (D-422).
  const visibleRows = shown.filter((l) => !(distributing && (l.charge || !!l.chargeKind)))
  const sectionOf = (l: TableLine): 'groups' | 'lines' | 'charges' | null =>
    l.kind === 'group' ? (l.chargeKind ? 'charges' : 'groups') : l.groupKey === null ? 'lines' : null
  const dividerBefore = new Set<string>()
  // The row just above each rule drops its own bottom hairline, and the rule's
  // row has none, so the dotted rule is the only line between two sections
  // (owner, 2026-10-28). A rule also opens the first section and closes the
  // last, so every section sits between two (owner, the same day: "every row
  // sits between some two dotted lines"); the header row then drops its
  // hairline too.
  const aboveDivider = new Set<string>()
  let lastKey: string | null = null
  if (nested) {
    let previous: string | null = null
    for (const l of visibleRows) {
      const section = sectionOf(l)
      if (section !== null) {
        if (section !== previous) {
          dividerBefore.add(l.key)
          if (lastKey !== null) aboveDivider.add(lastKey)
        }
        previous = section
      }
      lastKey = l.key
    }
    if (lastKey !== null) aboveDivider.add(lastKey)
  }
  const ruled = nested && lastKey !== null
  const columnCount =
    (bulk ? 1 : 0) + (expandColumn ? 1 : 0) + 1 + (showCode ? 1 : 0) + (showAccount ? 1 : 0) + (showDepartment ? 1 : 0) + 2 +
    (distributing ? 2 : 0) + 2
  const firstKey = visibleRows[0]?.key
  const ruleRow = (key: string, place: 'first' | 'between' | 'last' = 'between') => (
    <TableRow key={key} aria-hidden="true" className={mergeClasses(s.noRowRule, s.ruleRow)}>
      <TableCell
        colSpan={columnCount}
        className={mergeClasses(s.sectionDividerCell, place === 'first' && s.firstRuleCell, place === 'last' && s.lastRuleCell)}
      >
        <div className={s.sectionRule} />
      </TableCell>
    </TableRow>
  )
  const sectionDivider = (l: TableLine) =>
    dividerBefore.has(l.key) ? ruleRow(`divider-${l.key}`, l.key === firstKey ? 'first' : 'between') : null

  /** A GROUP ROW (G-19 to G-21), drawn by `LineGroupRow` in this table's cells. */
  function renderGroupRow(g: TableLine) {
    const key = g.groupKey as string
    const open = expanded.has(key)
    const members = g.members ?? []
    const sections = additionalDetailSections(labels.detailKind(g.accountId), null)
    const memberIds = members.map((m) => m.id)
    // FLEET MAINTENANCE'S DETAIL MAY DIFFER LINE TO LINE (owner, 2026-10-28 —
    // D-425): where any line carries it, the group's pop-up shows each field
    // reading "See specific line items.", never red.
    const fleetRollUp = members.some(
      (m) => additionalDetailSections(labels.detailKind(m.accountId), m.source).transportation && m.transportation !== null,
    )
    const groupDetailBase = additionalDetailState({
      sections,
      screen,
      // LOCKED (D-406): a line's own Additional Detail sets it.
      editable: { marketing: false },
      // G-8, D-406: a group some of whose lines have no Marketing Category
      // is red like one with none, and shows none.
      marketingCategoryId: g.views?.marketing_category_id.red ? null : g.marketing.categoryId,
      hasTransportationDetail: fleetRollUp,
    })
    // RED LIKE ITS ACCOUNT (owner, 2026-10-23 — D-416): locked, but a group
    // whose lines' Marketing Categories differ or are partly empty holds the
    // invoice back (G-9), so its icon wears the red border as the Account does.
    const categoryRed = !locked && sections.marketing && !!g.views?.marketing_category_id.red
    // THE TAX OR SHIPPING GROUP (D-420): fixed name, no Remove Group, and an
    // empty one has nothing to open, check or show tags for.
    const charge = !!g.chargeKind
    // ITS LINES' TAGS MUST AGREE (owner, 2026-10-28 — D-425): blue when they
    // share one, red while they differ — a line not saved yet carries none.
    const entityState = groupEntitiesState(
      memberIds.map((id) => entitiesByLineItem[id] ?? []),
      { charge, live: !locked },
    )
    const groupDetail = lineDetailIconState(
      categoryRed ? { ...groupDetailBase, requiredMissing: true, marketingInvalid: true } : groupDetailBase,
      entityState,
    )
    // A line not saved yet has no id a tag can hang on.
    const savedMemberIds = members.filter((m) => !m.isNew).map((m) => m.id)
    const checkedCount = memberIds.filter((id) => selection.selectedIds.has(id)).length
    return (
      <LineGroupRow
        key={g.key}
        group={{ ...g, views: g.views as NonNullable<TableLine['views']> }}
        charge={charge}
        empty={members.length === 0}
        classes={{
          selectCell: s.selectCell,
          cellInput: s.cellInput,
          codingCell: s.codingCell,
          codingCellInner: s.codingCellInner,
          pickerStack: s.pickerStack,
          num: s.num,
          derivedNum: s.derivedNum,
          iconCell: s.iconCell,
        }}
        columns={{ select: bulk, expand: expandColumn, code: showCode, account: showAccount, department: showDepartment, distributing }}
        // THE GROUP'S TAGS, READ-ONLY (owner, 2026-10-23 — D-409; D-425): the
        // Additional Detail pop-up lists the tags its saved lines share,
        // locked. A tag still goes on a line, never a group row (D-405).
        entities={
          invoice && savedMemberIds.length > 0
            ? {
                invoice,
                lineItem: { id: savedMemberIds[0] ?? '' } as InvoiceLineItem,
                lineItemIds: savedMemberIds,
                childMode: 'shared',
                editable: false,
              }
            : undefined
        }
        fleetRollUp={fleetRollUp}
        editing={editing}
        boxedFields={boxedFields}
        locked={locked}
        busy={applying}
        open={open}
        onToggle={() => toggleGroup(key)}
        grouping={groupingLive}
        // ITS BOX (D-406): checked when every line is, mixed when some are.
        checked={checkedCount === 0 ? false : checkedCount === memberIds.length ? true : 'mixed'}
        onCheck={(on) => selection.toggleMany(memberIds, on)}
        share={shares.get(g.key)}
        // THE GROUP'S YELLOW (D-417, narrowed by the owner 2026-10-27): the
        // expand arrow alone wears it while any line in the group carries an
        // unresolved flag, open or not; the flagged line's own field does too.
        flagged={editor !== undefined && linesHaveUnresolvedFlag(editor.flags, members.map((m) => m.number))}
        sections={sections}
        detail={groupDetail}
        accountName={(id) => labels.account(id)}
        departmentName={(id) => labels.department(id)}
        popupMountNode={popupMountNode}
        dropTarget={dropOver === g.key}
        rowProps={dragProps(g)}
        className={ruled || aboveDivider.has(g.key) ? s.noRowRule : undefined}
        onRenameInput={editor && !charge ? (name) => editor.renameGroup(key, name) : undefined}
        // NOT ON THE QUEUE CARD (owner, 2026-10-23 — D-409: "Description values
        // are ordinarily locked in the queue cards ... Let's just lock them all
        // there"): a group's name is renamed in the invoice modal.
        onRenameCommit={editor || !groupNamesEditable || charge ? undefined : (name) => void runGroup(() => undefined, { op: 'rename', group_id: g.id, name })}
        onUngroup={() => void runGroup(() => editor?.ungroup(key), { op: 'ungroup', group_id: g.id })}
        removable={!!editor && !lineValuesLocked && !charge}
        vendorId={groupVendorId}
      />
    )
  }

  return (
    <div className={editing ? undefined : s.panel}>
      <div className={s.tableScroll}>
        {/* THE BULK BOX (D-242): above the table once a line is checked — its
            own copy of the headers over the proposed row, in the table's
            scroller, so the two scroll together. */}
        {bulkError && !(bulk && selection.count > 0) && <ErrorBanner alert message={bulkError} />}
        {bulk && selection.count > 0 && (
          <LineItemBulkBar
            selected={bulkSelected}
            grouping={groupingLive}
            groups={groups.map((g) => ({ key: g.groupKey as string, name: g.description }))}
            vendorId={groupVendorId}
            scope={bulkScope ?? 'account'}
            screen={screen}
            columns={{ code: showCode, department: showDepartment, distributing }}
            header={lineHeader(false)}
            tableClassName={s.table}
            tableMinWidth={lineTableMinWidth}
            invoice={invoice}
            busy={applying}
            error={bulkError}
            mountNode={popupMountNode}
            onApply={applyBulk}
            onCombine={groupingLive ? combineSelected : undefined}
            onDelete={editor && !lineValuesLocked ? deleteSelected : undefined}
            onRemoveFromGroup={groupingLive ? removeSelectedFromGroups : undefined}
            onMoveToGroup={groupingLive ? moveSelectedToGroup : undefined}
            onClear={selection.clear}
          />
        )}
        <Table
          ref={tableRef}
          onFocusCapture={rememberCodingFocus}
          onBlurCapture={forgetCodingFocusOnDeparture}
          size="small"
          aria-label={editing ? 'Editable line items' : 'Line items'}
          className={s.table}
          style={{ minWidth: lineTableMinWidth }}
        >
          {lineHeader(true)}
          <TableBody>
            {visibleRows.flatMap((l) => [sectionDivider(l), (() => {
              if (l.kind === 'group') return renderGroupRow(l)
              // ADDITIONAL DETAIL (D-154): what this line shows — the Marketing
              // Category its account's kind owes, and Fleet Maintenance's own
              // detail on a line copied from it (D-381, read-only) — who may
              // write the category here, and what is red. A line added this
              // session has nothing stored to write against, so its pop-up
              // says to save first.
              const sections = additionalDetailSections(labels.detailKind(l.accountId), l.source)
              // A CHARGE LINE (D-420) carries no Account, Department or
              // Marketing Category: each is locked, blank and never red.
              const charge = !!l.charge
              const pickable = canPickCategories && !locked && l.saved && !charge
              const editable = { marketing: pickable && marketingCategoryEditableAt(screen) }
              const transportationDetail = sections.transportation ? l.transportation : null
              const other = additionalDetailState({
                sections,
                screen,
                editable,
                marketingCategoryId: l.marketing.categoryId,
                hasTransportationDetail: transportationDetail !== null,
              })
              // Associated Entities, in the same pop-up since D-425: written
              // here only in the editor (D-216). Yellow on an unmatched tag,
              // whose flag holds the release (D-452).
              const tagsEditable = editing && !locked
              const detailIcon = lineDetailIconState(other, associatedEntitiesState(entitiesByLineItem[l.id] ?? []))
              const live = !locked
              // Description, Price and Quantity: open in the editor unless the
              // status or a locked source closes them.
              const valuesLive = !lineValuesLocked
              const red = {
                description: showRequiredBorder('description', screen, isBlank(l.description), editing && valuesLive),
                // AN INACTIVE ACCOUNT COUNTS AS EMPTY wherever the Account can
                // still change (D-308): it must be changed before Release or
                // Approve. Where it is locked it is not red, and the invoice
                // proceeds as it is.
                account: showRequiredBorder(
                  'account',
                  screen,
                  !l.accountId || labels.accountInactive(l.accountId),
                  // Never red where it is locked (D-156) — past Import Control
                  // and Controller Approval, the Account is (D-305).
                  live && editsAccount && !charge,
                ),
                department: showRequiredBorder('department', screen, !l.departmentId, live && editsDepartment && !!l.accountId && !charge),
                price: showRequiredBorder('price', screen, isBlank(String(l.price ?? '')), editing && valuesLive),
                quantity: showRequiredBorder('quantity', screen, isBlank(String(l.quantity ?? '')), editing && valuesLive),
              }
              const saving = savingLineItemId === l.id
              const departmentBlocked =
                !l.accountId || (capitalOnlyAccounts.has(l.accountId) && !capitalEquipmentAllowed(floorTotalOf(l)))
              const priceKey = `price:${l.key}`
              // Under a group row only while the group rows show (D-408: its
              // handle stays neutral and its Description is indented).
              const member = nested && l.groupKey !== null
              return (
                <TableRow
                  key={l.key}
                  data-line-key={l.key}
                  className={mergeClasses(groupingLive && nested && s.draggable, dropOver === l.key && s.dropTarget, (ruled || aboveDivider.has(l.key)) && s.noRowRule)}
                  {...dragProps(l)}
                >
                  {bulk && (
                    <TableCell className={s.selectCell}>
                      <Checkbox
                        aria-label={`Select line ${l.number ?? ''}`}
                        checked={selection.selectedIds.has(l.id)}
                        disabled={applying}
                        onChange={(_e, d) => selection.toggle(l.id, d.checked === true)}
                      />
                    </TableCell>
                  )}
                  {expandColumn && (
                    <TableCell>
                      {/* THE DRAG HANDLE (D-406), where a group row shows its
                          arrow, wherever grouping may change; the row is what drags. */}
                      {groupingLive && nested && (
                        <span
                          className={mergeClasses(marks.mark, !member && marks.markBrand, marks.markHeavy, s.dragHandle)}
                          title="Drag onto a group or another line"
                          aria-hidden="true"
                        >
                          <ReOrderDotsVerticalRegular />
                        </span>
                      )}
                    </TableCell>
                  )}
                  <TableCell className={mergeClasses(member && s.memberIndent)}>
                    {editor ? (
                      <FlagTooltip text={lineTip(red.description, l, 'description')}>
                        <Input
                          className={mergeClasses(s.cellInput, lineBorder(red.description, l, 'description'))}
                          size="small"
                          aria-label="Description"
                          value={l.description}
                          onChange={(_, d) => editor.setDescription(l.id, d.value)}
                          onFocus={() => { if (valuesLive) editor.focusField(`description:${l.key}`, l.description) }}
                          onBlur={() => { if (valuesLive) editor.blurField(`description:${l.key}`, l.description) }}
                          onKeyDown={editor.onFieldKeyDown}
                          readOnly={lineValuesLocked}
                          appearance={lineValuesLocked ? 'filled-darker' : undefined}
                        />
                      </FlagTooltip>
                    ) : (
                      <LockedValue boxed={boxedFields} value={l.description} ariaLabel="Description" />
                    )}
                  </TableCell>
                  {showCode && (
                    <TableCell className={s.codingCell}>
                      <div className={s.codingCellInner}>{charge ? '' : lineItemCodingLabel(l.item, labels.account(l.accountId))}</div>
                    </TableCell>
                  )}
                  {showAccount && (
                    <TableCell className={s.codingCell}>
                      <div className={s.codingCellInner}>
                        <EditorTooltip editing={editing} text={lineTip(red.account, l, 'account')}>
                          <div className={s.pickerStack}>
                            {charge ? (
                              <LockedValue boxed={boxedFields} value="" ariaLabel="Account" />
                            ) : editsAccount ? (
                              <EntityPicker
                                key={`account:${pickerResetNonce}`}
                                size="small"
                                ariaLabel="Account"
                                describedBy={refusalFor(refusal, l.id, 'Account')}
                                value={{ id: l.accountId ?? undefined, name: l.accountLabel ?? labels.account(l.accountId) }}
                                mountNode={editing ? undefined : pickerMountNode}
                                // The column header asks the question; the
                                // placeholder says what to do (D-242).
                                placeholder={CODING_PICKER_PLACEHOLDER}
                                locked={locked}
                                // Busy, not disabled, while the line saves: a
                                // disabled box throws focus to the page (C2-06).
                                busy={saving}
                                // RED wherever a required value is missing
                                // (owner, 2026-08-18).
                                invalid={red.account}
                                className={lineBorder(red.account, l, 'account')}
                                search={(t) =>
                                  searchAccounts(t).then<EntityOption[]>((es) =>
                                    es.map((e) => ({ id: e.account_id, primary: e.account_name, secondary: e.account_id })),
                                  )
                                }
                                onSelect={(o) => (editor ? editor.pickAccount(l.id, o) : onAccountChange?.(l.item, o.id))}
                                // Emptied in the editor, the Account clears (D-295);
                                // the read-mode pickers put the stored one back.
                                onClear={editor ? () => editor.clearAccount(l.id) : undefined}
                              />
                            ) : (
                              <LockedValue boxed={boxedFields} value={labels.account(l.accountId) ?? '—'} ariaLabel="Account" />
                            )}
                          </div>
                        </EditorTooltip>
                      </div>
                    </TableCell>
                  )}
                  {showDepartment && (
                    <TableCell className={s.codingCell}>
                      <div className={s.codingCellInner}>
                        <EditorTooltip editing={editing} text={lineTip(red.department, l, 'department')}>
                          <div className={s.pickerStack}>
                            {charge ? (
                              <LockedValue boxed={boxedFields} value="" ariaLabel="Department" />
                            ) : editsDepartment ? (
                              <EntityPicker
                                key={`department:${pickerResetNonce}`}
                                size="small"
                                ariaLabel="Department"
                                describedBy={refusalFor(refusal, l.id, 'Department')}
                                value={{ id: l.departmentId ?? undefined, name: l.departmentLabel ?? labels.department(l.departmentId) }}
                                mountNode={editing ? undefined : pickerMountNode}
                                // ALWAYS "Search" (owner, 2026-09-30 — D-242). The
                                // box is blocked until the line has an account,
                                // and under the Capital Equipment floor where
                                // `190110` is its account's only department.
                                // Blocked, not disabled: the block can arrive
                                // after Tab from a saved Account has put focus
                                // here, and a disabled box drops it (review of
                                // C2-06).
                                placeholder={CODING_PICKER_PLACEHOLDER}
                                locked={locked}
                                blocked={!locked && departmentBlocked}
                                // Busy, not disabled, during the line's save, so
                                // Tab from a saved Account lands here (C2-06).
                                busy={saving}
                                // RED when required here and empty (D-156) —
                                // at Import Control too since D-309 — and only
                                // once the account is set.
                                invalid={red.department}
                                className={lineBorder(red.department, l, 'department')}
                                // The row's key is its id, so a new account
                                // reuses this picker; naming the dependency is
                                // what makes it re-query.
                                optionsKey={l.accountId ?? undefined}
                                search={(t) =>
                                  // This line's floor total (its own, or its
                                  // group's), so Capital Equipment is not offered
                                  // under the floor (item 8, D-403).
                                  searchDepartments(t, l.accountId ?? undefined, floorTotalOf(l)).then<EntityOption[]>((ds) =>
                                    ds.map((d) => ({ id: d.department_id, primary: d.department_name, secondary: d.department_id })),
                                  )
                                }
                                onSelect={(o) => (editor ? editor.pickDepartment(l.id, o) : onDepartmentChange?.(l.item, o.id))}
                              />
                            ) : (
                              <LockedValue boxed={boxedFields} value={labels.department(l.departmentId) ?? '—'} ariaLabel="Department" />
                            )}
                          </div>
                        </EditorTooltip>
                      </div>
                    </TableCell>
                  )}
                  {/* PRICE and QUANTITY are the inputs; TOTAL is derived from
                      them (D-4). `type="text"` for Price: a blurred box shows
                      "1,234.56", which `type="number"` would blank. */}
                  <TableCell className={s.num}>
                    {editor ? (
                      <FlagTooltip text={lineTip(red.price, l, 'price')}>
                        <Input
                          className={mergeClasses(s.cellInput, lineBorder(red.price, l, 'price'))}
                          size="small"
                          aria-label="Price"
                          type="text"
                          inputMode={lineValuesLocked ? undefined : 'decimal'}
                          contentBefore="$"
                          input={{ className: ms.moneyText }}
                          value={valuesLive && editingPrice.has(priceKey) ? String(l.price) : groupedPrice(String(l.price))}
                          onChange={(_, d) => editor.setAmounts(l.id, { price: filterMoneyInput(d.value) })}
                          onFocus={() => {
                            setEditingPrice((prev) => new Set(prev).add(priceKey))
                            if (valuesLive) editor.focusField(priceKey, String(l.price))
                          }}
                          onBlur={() => {
                            setEditingPrice((prev) => {
                              const next = new Set(prev)
                              next.delete(priceKey)
                              return next
                            })
                            if (!valuesLive) return
                            // Every decimal kept (D-249): 0.0049 stays 0.0049.
                            const f = priceText(String(l.price))
                            if (f !== l.price) editor.setAmounts(l.id, { price: f })
                            editor.blurField(priceKey, f)
                          }}
                          onKeyDown={editor.onFieldKeyDown}
                          readOnly={lineValuesLocked}
                          appearance={lineValuesLocked ? 'filled-darker' : undefined}
                        />
                      </FlagTooltip>
                    ) : (
                      <LockedValue boxed={boxedFields} value={formatPrice(l.price)} align="money" ariaLabel="Price" />
                    )}
                  </TableCell>
                  {/* Quantity is not money — no `$`, no grouping — but it
                      right-aligns beside the amounts (owner item 1, 2026-08-28). */}
                  <TableCell className={s.num}>
                    {editor ? (
                      <FlagTooltip text={lineTip(red.quantity, l, 'quantity')}>
                        <Input
                          className={mergeClasses(s.cellInput, lineBorder(red.quantity, l, 'quantity'))}
                          size="small"
                          aria-label="Quantity"
                          type={lineValuesLocked ? 'text' : 'number'}
                          input={{ className: ns.numText }}
                          value={String(l.quantity)}
                          onChange={(_, d) => editor.setAmounts(l.id, { quantity: d.value })}
                          onFocus={() => { if (valuesLive) editor.focusField(`quantity:${l.key}`, String(l.quantity)) }}
                          onBlur={() => { if (valuesLive) editor.blurField(`quantity:${l.key}`, String(l.quantity)) }}
                          onKeyDown={editor.onFieldKeyDown}
                          readOnly={lineValuesLocked}
                          appearance={lineValuesLocked ? 'filled-darker' : undefined}
                        />
                      </FlagTooltip>
                    ) : (
                      <LockedValue boxed={boxedFields} value={String(l.quantity ?? '—')} align="number" ariaLabel="Quantity" />
                    )}
                  </TableCell>
                  {/* The derived shares and the Total: locked everywhere (D-4).
                      The editor draws them as its own locked inputs. */}
                  {/* A line under a group shows its share of its group's, split
                      across the group's lines, so they add up to the group row
                      the CSV exports (D-403; owner, 2026-10-22). */}
                  {distributing && (
                    <TableCell className={boxedFields ? s.num : s.derivedNum}>
                      <DerivedMoney editing={editing} boxed={boxedFields} amount={shares.get(l.key)?.tax ?? 0} ariaLabel="Tax share" />
                    </TableCell>
                  )}
                  {distributing && (
                    <TableCell className={boxedFields ? s.num : s.derivedNum}>
                      <DerivedMoney editing={editing} boxed={boxedFields} amount={shares.get(l.key)?.shipping ?? 0} ariaLabel="Shipping share" />
                    </TableCell>
                  )}
                  <TableCell className={s.num}>
                    <DerivedMoney
                      editing={editing}
                      boxed={boxedFields}
                      amount={distributing ? (shares.get(l.key)?.total ?? toNumber(l.total)) : l.total}
                      ariaLabel="Total"
                    />
                  </TableCell>
                  <TableCell>
                    <div className={s.iconCell}>
                      <AdditionalDetailPopover
                        mountNode={popupMountNode}
                        sections={sections}
                        state={detailIcon}
                        hint={editing && !l.saved ? 'Save the invoice to fill these in.' : undefined}
                        marketing={{
                          categoryId: l.marketing.categoryId,
                          label: l.marketing.label,
                          onSelect: editable.marketing
                            ? (o) => (editor ? editor.pickCategory(l.id, o) : onCategoryChange?.(l.item, o.id))
                            : undefined,
                          saving,
                          pickerKey: `marketing:${pickerResetNonce}`,
                          describedBy: refusalFor(refusal, l.id, 'Marketing Category'),
                        }}
                        transportation={transportationDetail}
                        entities={
                          invoice
                            ? {
                                invoice,
                                lineItem: l.item,
                                editable: tagsEditable,
                                unsaved: editing ? l.isNew : undefined,
                                onChanged: editor ? editor.tagsChanged : undefined,
                              }
                            : undefined
                        }
                      />
                      {editor && !lineValuesLocked && (
                        <Button
                          appearance="subtle"
                          size="small"
                          icon={<DeleteRegular />}
                          aria-label="Remove Line Item"
                          onClick={() => editor.removeRow(l.id)}
                        />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )
            })()])}
            {ruled && ruleRow('divider-end', 'last')}
          </TableBody>
        </Table>
      </div>
      {editor && !lineValuesLocked && (
        <div className={s.addRow}>
          <Button appearance="subtle" size="small" icon={<AddRegular />} onClick={editor.addRow}>
            Add Line Item
          </Button>
        </div>
      )}
      {/* REAL LINES (D-403): a group row's total is its lines' sum. */}
      <ReconcileFooter
        lineItemsSum={lines.reduce((sum, l) => sum + toNumber(l.total), 0)}
        // Distributed: the coded lines plus everything spread over them (D-420).
        distributedTotal={distributing ? distributedGrandTotal(codedSum, spread.tax, spread.shipping, printedTotal) : 0}
        knownLinesSum={sumKnownLineTotals(lines.map((l) => l.total))}
        subtotal={subtotal}
        tax={tax}
        shipping={shipping}
        total={total}
        canDistribute={amountsKnown}
        distributing={distributing}
        distribute={distribute}
        onDistributeChange={setDistribute}
        // On every table, groups or none (D-410).
        canShowGroupRows
        showGroupRows={showGroupRows}
        onShowGroupRowsChange={setShowGroupRows}
        // The gate's own rule (G-9, D-406), not where the record is frozen.
        groupRowsBlock={
          !locked &&
          groups.some(
            (g) =>
              // A charge group never blocks: its name is fixed and its fields blank (D-420).
              !g.chargeKind &&
              (g.description.trim() === '' ||
              groupDisagrees(
                (g.members ?? []).map((m) => ({
                  account_id: m.accountId,
                  department_id: m.departmentId,
                  marketing_category_id: m.marketing.categoryId,
                })),
              ) ||
              // D-425: its lines' tags differ.
              groupTagsDisagree((g.members ?? []).map((m) => entitiesByLineItem[m.id] ?? []))),
          )
        }
      />
    </div>
  )
}

/** A derived amount — a charge share or a Total. The editor draws it as a
    locked input with a `$` before it, the read table as a locked box or text. */
export function DerivedMoney({
  editing,
  boxed,
  amount,
  ariaLabel,
  className,
}: {
  editing: boolean
  boxed: boolean
  amount: number | string | null
  ariaLabel: string
  /** Extra classes for the box, e.g. a group's yellow border (D-417). */
  className?: string
}) {
  const s = useStyles()
  const ms = useMoneyInputStyles()
  if (!editing) return <LockedValue boxed={boxed} value={formatCurrency(amount)} align="money" ariaLabel={ariaLabel} className={className} />
  return (
    <Input
      className={mergeClasses(s.cellInput, className)}
      size="small"
      type="text"
      contentBefore="$"
      input={{ className: ms.moneyText }}
      aria-label={ariaLabel}
      value={typeof amount === 'number' ? groupedMoney(amount.toFixed(2)) : groupedMoney(String(amount ?? ''))}
      readOnly
      appearance="filled-darker"
    />
  )
}

function ReconcileFooter({
  lineItemsSum,
  distributedTotal,
  knownLinesSum,
  subtotal,
  tax,
  shipping,
  total,
  canDistribute,
  distributing,
  distribute,
  onDistributeChange,
  canShowGroupRows = false,
  showGroupRows = true,
  onShowGroupRowsChange,
  groupRowsBlock = false,
}: {
  lineItemsSum: number
  /** The distributed rows' sum: the coded lines plus the spread Tax and
      Shipping, with the vendor's variance (D-151, D-420). Read while distributing. */
  distributedTotal: number
  /** What `checkAmounts` compares: the lines that carry an amount, or `null`
      when none does. */
  knownLinesSum: number | null
  subtotal?: number | string | null
  tax?: number | string | null
  shipping?: number | string | null
  total?: number | string | null
  canDistribute: boolean
  /** The toggle is on AND there is something to spread: decides the label
      and the figure. `distribute` alone decides the switch's position. */
  distributing: boolean
  distribute: boolean
  onDistributeChange: (next: boolean) => void
  /** Show Group Rows (owner, 2026-10-22): on every line table, groups or none (D-410). */
  canShowGroupRows?: boolean
  showGroupRows?: boolean
  onShowGroupRowsChange?: (next: boolean) => void
  /** A group holds the invoice back (its lines disagree, or it has no name). */
  groupRowsBlock?: boolean
}) {
  const s = useStyles()
  const ls = useSummaryLabelStyles()
  // The raw summed lines while off — the charge lines included (D-420); the
  // coded lines plus both spread charges while on — with the vendor's variance
  // of up to three cents, the figure the distributed rows and the CSV sum to
  // (D-151).
  const shown = distributing ? distributedTotal : lineItemsSum
  // ALWAYS enabled once it renders (owner item 4, 2026-08-18): spreading a
  // zero charge is just zero shares.
  const toggle = canDistribute ? (
    <Switch
      size="small"
      labelPosition="before"
      label="View With Tax &amp; Shipping Distributed"
      checked={distribute}
      onChange={(_e, d) => onDistributeChange(d.checked)}
    />
  ) : null
  // RED WHILE A HIDDEN GROUP BLOCKS (owner, 2026-10-23 — D-409): with the
  // group rows hidden, their red fields cannot be seen, so the switch that
  // shows them carries the red border instead.
  const hiddenBlock = groupRowsBlock && !showGroupRows
  const groupToggle =
    canShowGroupRows && onShowGroupRowsChange ? (
      <Switch
        className={mergeClasses(hiddenBlock && s.toggleBlocked)}
        size="small"
        labelPosition="before"
        label="Show Group Rows"
        title={hiddenBlock ? 'A group needs attention. Show the group rows to see it.' : undefined}
        checked={showGroupRows}
        onChange={(_e, d) => onShowGroupRowsChange(d.checked)}
      />
    ) : null
  // Without invoice amounts there is nothing to reconcile against: no pill,
  // the same figure under the same label (owner, 2026-08-14).
  if (subtotal == null && tax == null && total == null && shipping == null) {
    return (
      <div className={s.footer}>
        {groupToggle && <div className={s.footerStart}>{groupToggle}</div>}
        <span className={ls.label}>Invoice Subtotal:</span>
        <span className={ls.amount}>{formatCurrency(lineItemsSum)}</span>
      </div>
    )
  }
  // The figure is the lines' own sum, labelled as such (owner, 2026-08-14).
  // The pill reads the RAW sum, never the distributed one: a view preference
  // must not turn a pill from red to green.
  const { reconciles } = checkAmounts(knownLinesSum, tax, total, shipping)
  return (
    <div className={s.footer}>
      {groupToggle && <div className={s.footerStart}>{groupToggle}</div>}
      <div className={s.footerControls}>
        {toggle}
        <DotPill pill={reconciles ? TINT_PILLS.green : TINT_PILLS.red}>
          {reconciles ? 'Reconciles' : "Doesn't reconcile"}
        </DotPill>
        <span className={ls.label}>{distributing ? 'Invoice Total:' : 'Invoice Subtotal:'}</span>
      </div>
      <span className={ls.amount}>{formatCurrency(shown)}</span>
    </div>
  )
}
